import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Lock, AlertOctagon, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useSystemStore } from '@/stores/system';
import { adminEmergency } from '@/api/admin';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { ApiError } from '@/lib/http';
import { fmtDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

function fmtRemaining(unlockAt: string | null): string {
  if (!unlockAt) return '';
  const ms = new Date(unlockAt).getTime() - Date.now();
  if (ms <= 0) return '即将解锁';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `还剩 ${h}h ${m}m`;
  if (m > 0) return `还剩 ${m}m ${s}s`;
  return `还剩 ${s}s`;
}

/** 后端 409 未处置敞口的 details 形态 */
interface BlockInfo {
  mainOpenPositions: number;
  mainLiquidationFailed: boolean;
}

/**
 * 全局告示条 — 任一为 true 时悬挂在 TopBar 下方:
 * - 紧急停机激活(附「解除紧急停机」操作 → 恢复跟单 / 雇员下单)
 * - 交易锁定中(强平 / 时段外),含解锁倒计时
 *
 * 解除走 `DELETE /admin/api/v1/emergency-stop`。
 * 后端有安全闸:主账户仍有 OPEN 持仓或上次强平失败未复核时返回 409,需勾选 force 强制解除。
 * 成功后乐观更新 store 即时让横幅消失,WS 广播 `emergency_stop_cleared` 再兜底一致。
 */
export function TradeLockedBanner() {
  const emergencyStop = useSystemStore((s) => s.emergencyStop);
  const reason = useSystemStore((s) => s.emergencyReason);
  const tradeLocked = useSystemStore((s) => s.tradeLocked);
  const unlockAt = useSystemStore((s) => s.unlockAt);
  const setEmergencyStop = useSystemStore((s) => s.setEmergencyStop);

  const [confirmOpen, setConfirmOpen] = useState(false);
  // 后端 409 命中未处置敞口时切到强制解除模式,展示拦截详情
  const [block, setBlock] = useState<BlockInfo | null>(null);

  function resetDialog() {
    setConfirmOpen(false);
    setBlock(null);
  }

  const clearMut = useMutation({
    mutationFn: (force: boolean) => adminEmergency.clearEmergencyStop({ force }),
    onSuccess: () => {
      setEmergencyStop(false);
      resetDialog();
      toast.success('已解除紧急停机', { description: '跟单引擎与雇员下单已恢复' });
    },
    onError: (err) => {
      // 安全闸:未处置敞口 → 切到强制解除模式,展示拦截原因,等管理员二次确认
      if (err instanceof ApiError && err.status === 409 && err.details?.['requireForce']) {
        setBlock({
          mainOpenPositions: Number(err.details['mainOpenPositions'] ?? 0),
          mainLiquidationFailed: Boolean(err.details['mainLiquidationFailed']),
        });
        toast.warning('存在未处置敞口', { description: err.message });
        return;
      }
      // 网络/超时类错误:解除结果未知(回包可能已在服务端成功,只是链路中断把响应吞了 ——
      // 2026-07-03 解除卡死事故即此)。不武断报「失败」,交给 dashboard 5s 轮询据服务端真实标志
      // 兜底对账:若已解除,横幅会在数秒内自动消失(本对话框随之卸载);否则管理员可重试。
      if (err instanceof ApiError && (err.code === 'NETWORK' || err.status === 0)) {
        toast.warning('网络波动,正在核对解除结果', {
          description: '若已解除,横幅将在数秒内自动消失;若未消失请重试',
        });
        return;
      }
      toast.error('解除失败', {
        description: err instanceof ApiError ? err.message : '请稍后重试',
      });
    },
  });

  const [, setTick] = useState(0);
  useEffect(() => {
    if (!tradeLocked) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [tradeLocked]);

  if (!emergencyStop && !tradeLocked) return null;

  const forceMode = block !== null;

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-2 text-sm border-b',
          emergencyStop
            ? 'bg-down/10 text-down-dim border-down/30 animate-pulse-glow'
            : 'bg-warn/10 text-warn-dim border-warn/30',
        )}
        role="alert"
      >
        {emergencyStop ? <AlertOctagon className="h-4 w-4 shrink-0" /> : <Lock className="h-4 w-4 shrink-0" />}
        <span className="font-semibold">{emergencyStop ? '紧急停机已触发' : '交易锁定中'}</span>
        <span className="text-ink-2">·</span>
        <span className="truncate flex items-center gap-2">
          {emergencyStop ? (
            reason || '所有跟单已暂停,请前往「告警中心」查看原因'
          ) : (
            <>
              <span>解锁时间 {fmtDateTime(unlockAt)}</span>
              {unlockAt && (
                <span className="num font-medium px-1.5 py-0.5 rounded bg-warn/20 text-warn-dim">
                  {fmtRemaining(unlockAt)}
                </span>
              )}
            </>
          )}
        </span>
        {emergencyStop && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto shrink-0 border-down/40 text-down-dim hover:bg-down/15"
            onClick={() => setConfirmOpen(true)}
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            解除紧急停机
          </Button>
        )}
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(v) => !v && !clearMut.isPending && resetDialog()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-down">
              <ShieldCheck className="h-5 w-5" />
              解除紧急停机
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2">
                <span className="block">
                  解除后跟单引擎与雇员下单将<strong>立即恢复</strong>。请先确认触发原因已排除
                  (前往「告警中心」查看),否则风控监控可能很快再次自动停机。
                </span>
                {reason && <span className="block text-xs text-ink-2 num">当前停机原因:{reason}</span>}
              </div>
            </DialogDescription>
          </DialogHeader>

          {forceMode && (
            <div className="space-y-1.5 rounded-md border border-down/40 bg-down/10 p-3 text-xs text-down-dim">
              <div className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle className="h-3.5 w-3.5" />
                检测到未处置敞口,已拦截常规解除
              </div>
              <ul className="list-disc space-y-0.5 pl-4">
                {block.mainOpenPositions > 0 && (
                  <li>
                    主账户仍有 <span className="num font-semibold">{block.mainOpenPositions}</span> 个 OPEN 持仓未平
                  </li>
                )}
                {block.mainLiquidationFailed && <li>上次强平失败(MAIN_LIQUIDATION_FAILED)尚未人工复核</li>}
              </ul>
              <div>
                请先在 cTrader / 后台把主账户敞口平干净再解除。
                <strong>确认真实敞口已平</strong>后,可强制解除(将记入审计)。
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={resetDialog} disabled={clearMut.isPending}>
              取消
            </Button>
            {forceMode ? (
              <Button
                variant="destructive"
                onClick={() => clearMut.mutate(true)}
                disabled={clearMut.isPending}
              >
                {clearMut.isPending ? '强制解除中…' : '强制解除'}
              </Button>
            ) : (
              <Button onClick={() => clearMut.mutate(false)} disabled={clearMut.isPending}>
                {clearMut.isPending ? '解除中…' : '确认解除'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
